import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Sparkles,
  Send,
  Plus,
  Trash2,
  MessageCircle,
  Compass,
  Heart,
  BookOpen,
  Newspaper,
  Loader2,
  Volume2,
  VolumeX,
  ChevronLeft,
  Settings,
  Mic,
  MicOff,
  Square,
} from "lucide-react";
import { toast } from "sonner";

const modeIcons = {
  general: MessageCircle,
  advice: Compass,
  emotional_support: Heart,
  information: BookOpen,
  news: Newspaper,
};

const modeColors = {
  general: "bg-amber-500",
  advice: "bg-blue-500",
  emotional_support: "bg-pink-500",
  information: "bg-green-500",
  news: "bg-purple-500",
};

export default function Joy() {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedMode, setSelectedMode] = useState("general");
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // TTS State
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [playingAudio, setPlayingAudio] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(null);
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const audioRef = useRef(null);

  // Speech-to-Text State
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success("Recording started - speak now!");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Transcribe recorded audio
  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/stt/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.text) {
        setNewMessage(prev => prev + (prev ? ' ' : '') + response.data.text);
        toast.success("Voice transcribed!");
        inputRef.current?.focus();
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error("Failed to transcribe audio");
    } finally {
      setIsTranscribing(false);
    }
  };

  // Fetch modes and voices
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [modesRes, voicesRes] = await Promise.all([
          axios.get(`${API}/joy/modes`),
          axios.get(`${API}/tts/voices`),
        ]);
        setModes(modesRes.data.modes);
        setVoices(voicesRes.data.voices);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/joy/conversations`);
      setConversations(response.data.conversations);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Load conversation messages
  const loadConversation = async (convId) => {
    try {
      const response = await axios.get(`${API}/joy/conversations/${convId}`);
      setCurrentConversation(response.data);
      setMessages(response.data.messages || []);
      setShowSidebar(false);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };

  // Create new conversation
  const createConversation = async () => {
    try {
      const response = await axios.post(`${API}/joy/conversations`);
      setCurrentConversation(response.data);
      setMessages([]);
      setShowSidebar(false);
      fetchConversations();
      inputRef.current?.focus();
    } catch (error) {
      toast.error("Failed to create conversation");
    }
  };

  // Delete conversation
  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!confirm("Delete this conversation?")) return;
    try {
      await axios.delete(`${API}/joy/conversations/${convId}`);
      if (currentConversation?.id === convId) {
        setCurrentConversation(null);
        setMessages([]);
      }
      fetchConversations();
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !currentConversation) return;

    setSending(true);
    const userMsg = newMessage;
    setNewMessage("");

    try {
      const response = await axios.post(
        `${API}/joy/chat/${currentConversation.id}`,
        { content: userMsg, mode: selectedMode }
      );

      setMessages((prev) => [
        ...prev,
        response.data.user_message,
        response.data.assistant_message,
      ]);

      fetchConversations();

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      toast.error("Failed to send message");
      setNewMessage(userMsg);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Text-to-Speech
  const speakText = async (text, messageId) => {
    if (playingAudio === messageId) {
      // Stop current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudio(null);
      return;
    }

    setLoadingAudio(messageId);
    try {
      const response = await axios.post(`${API}/tts/speak`, {
        text: text.substring(0, 4096),
        voice: selectedVoice,
        speed: 1.0,
      });

      const audioData = response.data.audio;
      const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAudio(null);
        audioRef.current = null;
      };

      audio.onerror = () => {
        toast.error("Failed to play audio");
        setPlayingAudio(null);
        audioRef.current = null;
      };

      setPlayingAudio(messageId);
      audio.play();
    } catch (error) {
      toast.error("Failed to generate speech");
    } finally {
      setLoadingAudio(null);
    }
  };

  // Preview voice
  const previewVoice = async (voiceId) => {
    setLoadingAudio(`preview-${voiceId}`);
    try {
      const response = await axios.get(`${API}/tts/preview/${voiceId}`);
      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
      audio.play();
    } catch (error) {
      toast.error("Failed to preview voice");
    } finally {
      setLoadingAudio(null);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const ModeIcon = modeIcons[selectedMode] || MessageCircle;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      <Navigation />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="flex gap-4 h-[calc(100vh-200px)]">
          {/* Conversations Sidebar */}
          <div
            className={`${
              showSidebar ? "block" : "hidden md:block"
            } w-full md:w-80 flex-shrink-0`}
          >
            <Card className="h-full bg-white/90 backdrop-blur">
              <CardContent className="p-0 h-full flex flex-col">
                {/* Header */}
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between mb-3">
                    <h1 className="text-xl font-bold flex items-center gap-2" data-testid="joy-title">
                      <Sparkles className="w-6 h-6 text-amber-500" />
                      Joy AI
                    </h1>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setVoiceSettingsOpen(true)}
                      data-testid="voice-settings-btn"
                    >
                      <Settings className="w-5 h-5" />
                    </Button>
                  </div>
                  <Button
                    onClick={createConversation}
                    className="w-full bg-amber-500 hover:bg-amber-600"
                    data-testid="new-conversation-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Conversation
                  </Button>
                </div>

                {/* Conversation List */}
                <ScrollArea className="flex-1">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">
                        Start a conversation with Joy!
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {conversations.map((conv) => (
                        <button
                          key={conv.id}
                          onClick={() => loadConversation(conv.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                            currentConversation?.id === conv.id
                              ? "bg-amber-100"
                              : "hover:bg-gray-50"
                          }`}
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {conv.title}
                            </p>
                            <p className="text-xs text-gray-400">
                              {conv.message_count} messages
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 hover:bg-red-100 hover:text-red-500"
                            onClick={(e) => deleteConversation(conv.id, e)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <div
            className={`${
              !showSidebar ? "block" : "hidden md:block"
            } flex-1 flex flex-col`}
          >
            <Card className="flex-1 bg-white/90 backdrop-blur flex flex-col">
              {currentConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="flex items-center gap-3 p-4 border-b">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden"
                      onClick={() => setShowSidebar(true)}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Avatar className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500">
                      <AvatarFallback className="bg-transparent">
                        <Sparkles className="w-5 h-5 text-white" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h2 className="font-semibold">Joy</h2>
                      <p className="text-xs text-gray-500">
                        Your AI companion for positivity
                      </p>
                    </div>
                    <Select value={selectedMode} onValueChange={setSelectedMode}>
                      <SelectTrigger className="w-40" data-testid="mode-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modes.map((mode) => {
                          const Icon = modeIcons[mode.id] || MessageCircle;
                          return (
                            <SelectItem key={mode.id} value={mode.id}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {mode.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    {messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-4">
                          <Sparkles className="w-10 h-10 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">
                          Hi, I'm Joy!
                        </h3>
                        <p className="text-gray-500 max-w-md">
                          I'm here to chat, offer advice, provide emotional support,
                          answer questions, or discuss the news. How can I help you
                          today?
                        </p>
                        <div className="flex flex-wrap gap-2 mt-4 justify-center">
                          {modes.slice(0, 4).map((mode) => {
                            const Icon = modeIcons[mode.id] || MessageCircle;
                            return (
                              <Badge
                                key={mode.id}
                                variant="outline"
                                className={`cursor-pointer hover:bg-gray-100 px-3 py-1`}
                                onClick={() => setSelectedMode(mode.id)}
                              >
                                <Icon className="w-3 h-3 mr-1" />
                                {mode.name}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid="joy-messages">
                        {messages.map((msg) => {
                          const isUser = msg.role === "user";
                          const MsgModeIcon = modeIcons[msg.mode] || MessageCircle;
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${
                                isUser ? "justify-end" : "justify-start"
                              }`}
                              data-testid={`joy-message-${msg.id}`}
                            >
                              <div
                                className={`max-w-[80%] flex items-end gap-2 ${
                                  isUser ? "flex-row-reverse" : ""
                                }`}
                              >
                                {!isUser && (
                                  <Avatar className="w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 flex-shrink-0">
                                    <AvatarFallback className="bg-transparent">
                                      <Sparkles className="w-4 h-4 text-white" />
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                <div>
                                  <div
                                    className={`rounded-2xl px-4 py-2 ${
                                      isUser
                                        ? "bg-amber-500 text-white rounded-br-none"
                                        : "bg-gray-100 text-gray-900 rounded-bl-none"
                                    }`}
                                  >
                                    <p className="text-sm whitespace-pre-wrap">
                                      {msg.content}
                                    </p>
                                  </div>
                                  <div
                                    className={`flex items-center gap-2 mt-1 text-xs ${
                                      isUser ? "justify-end" : ""
                                    }`}
                                  >
                                    <span className="text-gray-400">
                                      {formatTime(msg.created_at)}
                                    </span>
                                    {!isUser && (
                                      <>
                                        <Badge
                                          variant="outline"
                                          className="text-xs px-1.5 py-0"
                                        >
                                          <MsgModeIcon className="w-3 h-3" />
                                        </Badge>
                                        <button
                                          onClick={() =>
                                            speakText(msg.content, msg.id)
                                          }
                                          className="text-gray-400 hover:text-amber-500 transition-colors"
                                          data-testid={`speak-${msg.id}`}
                                        >
                                          {loadingAudio === msg.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : playingAudio === msg.id ? (
                                            <VolumeX className="w-4 h-4 text-amber-500" />
                                          ) : (
                                            <Volume2 className="w-4 h-4" />
                                          )}
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  {/* Input Area */}
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <Textarea
                        ref={inputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={`Ask Joy anything... (${
                          modes.find((m) => m.id === selectedMode)?.name || "General"
                        } mode)`}
                        className="min-h-[60px] resize-none rounded-2xl"
                        data-testid="joy-input"
                      />
                      {/* Voice Input Button */}
                      <Button
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isTranscribing}
                        variant={isRecording ? "destructive" : "outline"}
                        className={`h-auto rounded-2xl px-4 ${isRecording ? "animate-pulse" : ""}`}
                        data-testid="joy-voice-btn"
                      >
                        {isTranscribing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : isRecording ? (
                          <Square className="w-5 h-5" />
                        ) : (
                          <Mic className="w-5 h-5" />
                        )}
                      </Button>
                      {/* Send Button */}
                      <Button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="h-auto rounded-2xl bg-amber-500 hover:bg-amber-600 px-6"
                        data-testid="joy-send-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                    {isRecording && (
                      <p className="text-sm text-red-500 mt-2 text-center animate-pulse">
                        Recording... Click the stop button when done
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-6">
                    <Sparkles className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-700 mb-2">
                    Meet Joy
                  </h2>
                  <p className="text-gray-500 text-center max-w-md mb-6">
                    Your AI companion for life advice, emotional support,
                    information, and positive conversations.
                  </p>
                  <Button
                    onClick={createConversation}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="start-conversation-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Start a Conversation
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </main>

      {/* Voice Settings Dialog */}
      <Dialog open={voiceSettingsOpen} onOpenChange={setVoiceSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-amber-500" />
              Voice Settings
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Choose a voice for Joy to speak with when you click the speaker
              icon on messages.
            </p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {voices.map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => setSelectedVoice(voice.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                    selectedVoice === voice.id
                      ? "bg-amber-100 border-2 border-amber-500"
                      : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
                  }`}
                  data-testid={`voice-${voice.id}`}
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{voice.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {voice.gender}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{voice.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      previewVoice(voice.id);
                    }}
                    data-testid={`preview-${voice.id}`}
                  >
                    {loadingAudio === `preview-${voice.id}` ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </Button>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setVoiceSettingsOpen(false)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
